import { useEffect, useMemo } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { parseCharte, generatePalette, paletteToCssVars } from '@/lib/palette';

// ─────────────────────────────────────────────────────────────
// Applique les couleurs du site public (boutique/marketplace) aux
// pages « client » (connexion, création de compte, mon compte).
//
// • Sur un sous-domaine (ou domaine personnalisé) → le compte prend
//   les couleurs de la boutique sur laquelle on s'est connecté.
// • Sur zifek.fr directement → aucune surcharge, on garde les
//   couleurs Zifek par défaut.
// ─────────────────────────────────────────────────────────────

function hexToOklchChannels(hex: string): string | null {
  const h = (hex || '').replace('#', '').trim();
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const x = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
  const y = 0.2126729 * lr + 0.7151522 * lg + 0.072175 * lb;
  const z = 0.0193339 * lr + 0.119192 * lg + 0.9503041 * lb;

  const x_ = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
  const y_ = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
  const z_ = 0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z;

  const xr = Math.cbrt(x_);
  const yr = Math.cbrt(y_);
  const zr = Math.cbrt(z_);

  const L = 0.2104542553 * xr + 0.793617785 * yr - 0.0040720468 * zr;
  const a = 1.9779984951 * xr - 2.428592205 * yr + 0.4505937099 * zr;
  const b2 = 0.0259040371 * xr + 0.7827717662 * yr - 0.808675766 * zr;

  const C = Math.sqrt(a * a + b2 * b2);
  let H = (Math.atan2(b2, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return `${L.toFixed(5)} ${C.toFixed(6)} ${H.toFixed(6)}`;
}

export default function TenantColorBridge() {
  const { isTenant, theme, tenant } = useTenant();
  const primaryColor = theme?.primaryColor;
  const accentColor = theme?.accentColor;
  const chartCss = theme?.chartPaletteCss;

  // Repli : quand aucun thème n'est appliqué (design natif), on reprend la
  // charte graphique du commerce définie dans son tableau de bord pour que
  // TOUS les boutons du design natif soient à ses couleurs.
  const fallbackPalette = useMemo(() => {
    const charte = parseCharte(tenant?.couleurcharte);
    return charte ? generatePalette(charte) : null;
  }, [tenant?.couleurcharte]);

  useEffect(() => {
    if (!isTenant) return;
    const parts: string[] = [];
    const css = chartCss || (fallbackPalette ? paletteToCssVars(fallbackPalette) : '');
    if (css) parts.push(css);
    const overrides: string[] = [];
    const primaryHex = primaryColor || fallbackPalette?.primary;
    const accentHex = accentColor || fallbackPalette?.accent;
    const secondaryHex = fallbackPalette?.secondary;
    const primary = primaryHex ? hexToOklchChannels(primaryHex) : null;
    const primaryDark = fallbackPalette?.primaryDark ? hexToOklchChannels(fallbackPalette.primaryDark) : null;
    const accent = accentHex ? hexToOklchChannels(accentHex) : null;
    const accentDark = fallbackPalette?.accentDark ? hexToOklchChannels(fallbackPalette.accentDark) : null;
    const secondary = secondaryHex ? hexToOklchChannels(secondaryHex) : null;
    if (primary) overrides.push(`--primary-500: ${primary};`);
    if (primaryDark) overrides.push(`--primary-600: ${primaryDark};`);
    if (accent) overrides.push(`--accent-500: ${accent};`);
    if (accentDark) overrides.push(`--accent-600: ${accentDark};`);
    if (secondary) overrides.push(`--secondary-500: ${secondary};`);
    if (overrides.length > 0) parts.push(`:root { ${overrides.join(' ')} }`);
    if (parts.length === 0) return;

    const style = document.createElement('style');
    style.id = 'zifek-tenant-color-bridge';
    style.textContent = parts.join('\n');
    document.head.appendChild(style);
    return () => style.remove();
  }, [isTenant, primaryColor, accentColor, chartCss, fallbackPalette]);

  return null;
}